import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { getItem, setItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Layout from "../component/layout";
import { get_pm_invoiceURL } from "../../../utils/urls";
import { useFacilityStore } from "../../../app/z-store/store";
import InvoiceActionsDropdown from "../levy_management/Invoicing/invoice_management/InvoiceActionsDropdown";
import styles from "./pmInvoice.module.css";

const PMInvoicePage = () => {
  // State management
  const routerLocation = useLocation();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [facilityLogo, setFacilityLogo] = useState(
    () => localStorage.getItem("selectedFacilityLogo") || "",
  );
  const [showReceiptsDialog, setShowReceiptsDialog] = useState(false);

  // Hooks
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const facilityId = useFacilityStore((state) => state.facilityId);

  // ─── Voided helper ───────────────────────────────────────────────────────────
  const isVoided = invoice?.status === "Void";

  // Fetch data on component mount
  useEffect(() => {
    fetchInvoiceData();
    getFacilityLogo();
  }, [invoiceId, facilityId]);

  const getFacilityLogo = async () => {
    try {
      const facilityFromState = routerLocation.state?.facility;
      if (facilityFromState?.logo) {
        await setItem("selectedFacilityLogo", facilityFromState.logo);
        setFacilityLogo(facilityFromState.logo);
      } else {
        const storedLogo = await getItem("selectedFacilityLogo");
        setFacilityLogo(storedLogo || "");
      }
    } catch (error) {
      console.error("Error fetching facility logo:", error);
      const fallbackLogo = localStorage.getItem("selectedFacilityLogo");
      setFacilityLogo(fallbackLogo || "");
    }
  };

  // Fetch invoice data
  const fetchInvoiceData = async () => {
    setLoading(true);
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(
        `${get_pm_invoiceURL}/${facilityId}/${invoiceId}`,
        "GET",
      );

      console.log("PM INVOICE RESPONSE", response);

      if (response.success) {
        const invoiceData = response.data?.data || response.data;
        setInvoice(invoiceData);
      } else {
        toastify(response.message || "Failed to fetch invoice", "error");
      }
    } catch (error) {
      console.error("Error fetching invoice:", error);
      toastify("Error fetching invoice", "error");
    } finally {
      setLoading(false);
    }
  };

  const markInvoiceAsRead = async () => {
    setLoading(true);
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(
        `${get_pm_invoiceURL}/${facilityId}/${invoiceId}/markAsRead`,
        "POST",
      );
      if (response.success) {
        setInvoice((prev) => ({
          ...prev,
          viewStatus: { ...prev.viewStatus, isOpened: true, openedAt: new Date() },
        }));
        toastify("Invoice marked as read", "success");
      } else {
        toastify(response.message || "Failed to mark invoice as read", "error");
      }
    } catch (error) {
      toastify("Error marking invoice as read", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    const element = document.getElementById("printable-invoice");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const docLabel = isVoided ? "CreditNote" : "Invoice";
    const filename = `${docLabel}_${invoice?.invoiceNumber}_${timestamp}.pdf`;

    try {
      setLoading(true);
      toastify("Generating PDF...", "info");

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");

      let heightLeft = imgHeight - pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
      toastify("PDF downloaded successfully!", "success");
    } catch (error) {
      console.error("PDF generation error:", error);
      toastify("Failed to generate PDF. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handler for cash payment success - refreshes invoice data
  const handleCashPaymentSuccess = () => {
    fetchInvoiceData();
  };

  const viewReceipt = (paymentData) => {
    const allowedTypes = ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"];
    const paymentType = (paymentData.type || "").toLowerCase();
    const isAllowedType = allowedTypes.some(
      (type) => paymentType === type || paymentType.includes(type.replace("-", "")),
    );
    if (!isAllowedType) {
      toastify(
        "Receipt viewing is only available for cash, M-Pesa, bank transfers, and cheque payments",
        "info",
      );
      return;
    }
    navigate(`/facility/levy_management/receipt/${paymentData._id}`, {
      state: { receipt: paymentData, invoice },
    });
  };

  const actionBodyTemplate = (rowData) => {
    const allowedTypes = ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"];
    const paymentType = (rowData.type || "").toLowerCase();
    const isAllowedType = allowedTypes.some(
      (type) => paymentType === type || paymentType.includes(type.replace("-", "")),
    );
    return (
      <Button
        icon="fas fa-eye"
        className="p-button-sm p-button-text"
        onClick={() => viewReceipt(rowData)}
        disabled={!isAllowedType}
        tooltip={isAllowedType ? "View Receipt" : "Receipt not available for this payment type"}
        tooltipOptions={{ position: "left" }}
      />
    );
  };

  // ─── Balance calculations ─────────────────────────────────────────────────────
  const hasBalanceBroughtForward = () =>
    invoice?.balanceBroughtForward !== 0 &&
    invoice?.balanceBroughtForward !== null &&
    invoice?.balanceBroughtForward !== undefined;

  const isCredit = invoice?.balanceBroughtForward < 0;

  const calculateTotalBalance = () => {
    if (!invoice) return 0;
    return (
      (parseFloat(invoice.totalAmount) || 0) +
      (parseFloat(invoice.balanceBroughtForward) || 0) +
      (parseFloat(invoice.penalty) || 0)
    );
  };

  const calculateTotalDue = () => {
    if (!invoice) return 0;
    return calculateTotalBalance() - (invoice.amountPaid || 0);
  };

  const isFinalCredit = calculateTotalDue() < 0;

  const formatBalanceBroughtForward = () => {
    if (!invoice?.balanceBroughtForward || invoice.balanceBroughtForward === 0)
      return formatAmount(0);
    return formatAmount(Math.abs(invoice.balanceBroughtForward));
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const formatDate = (date) => {
    if (!date) return "N/A";
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleDateString("en-US", { month: "short" });
    const year = dateObj.getFullYear();
    const getOrdinalSuffix = (d) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    const currencyCode = invoice?.currency?.code || "KES";
    const numericAmount = parseFloat(amount);
    const formattedNumber = numericAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currencyCode} ${formattedNumber}`;
  };

  // ─── Read/Unread styles ───────────────────────────────────────────────────────
  const readStatusStyles = {
    button: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0.5rem 1rem",
      borderRadius: "20px",
      fontSize: "0.875rem",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginLeft: "0.75rem",
    },
    unread: { backgroundColor: "#ffc107", color: "#212529" },
    read: { backgroundColor: "#28a745", color: "#ffffff" },
  };

  // ─── Loading guard ────────────────────────────────────────────────────────────
  if (loading && !invoice) {
    return (
      <Layout>
        <div className={styles.loading}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading invoice details...</span>
        </div>
      </Layout>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className={styles.pageContainer}>

        {/* ── Action Bar ─────────────────────────────────────────────────────── */}
        <div className={styles.actionCard}>
          <div className={styles.actionCardLeft}>
            <Link to="#" onClick={() => navigate(-1)} className={styles.backLink}>
              <i className="ti ti-arrow-narrow-left"></i>
              <span>Back</span>
            </Link>

            {/* Read / Unread status */}
            <div style={{ display: "inline-block", marginLeft: "20px" }}>
              {invoice?.viewStatus?.isOpened === true ? (
                <button style={{ ...readStatusStyles.button, ...readStatusStyles.read }}>
                  <i className="fas fa-check-circle me-1"></i>Read
                </button>
              ) : (
                <button
                  style={{ ...readStatusStyles.button, ...readStatusStyles.unread }}
                  onClick={markInvoiceAsRead}
                >
                  <i className="fas fa-exclamation-circle me-1"></i>Unread
                </button>
              )}
            </div>
          </div>

          <div className={styles.actionCardRight}>
            {/* Use the original InvoiceActionsDropdown component */}
            <InvoiceActionsDropdown
              invoice={invoice}
              facilityId={facilityId}
              handleDownloadPDF={handleDownloadPDF}
              setShowReceiptsDialog={setShowReceiptsDialog}
              onCashPaymentSuccess={handleCashPaymentSuccess}
              loading={loading}
              isVoided={isVoided}
            />
          </div>
        </div>

        {/* ── Receipts Dialog ───────────────────────────────────────────────────── */}
        <Dialog
          header="Payment Receipts"
          visible={showReceiptsDialog}
          style={{ width: "50vw" }}
          onHide={() => setShowReceiptsDialog(false)}
          resizable={false}
        >
          <DataTable
            value={invoice?.reconciliationHistory || []}
            responsiveLayout="scroll"
            emptyMessage="No payment records found"
            className="p-datatable-sm"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
          >
            <Column
              field="date"
              header="Date"
              body={(rowData) => formatDate(rowData.date)}
              sortable
            />
            <Column
              field="amount"
              header="Amount"
              body={(rowData) => formatAmount(rowData.amount)}
              sortable
            />
            <Column
              field="type"
              header="Type"
              sortable
              body={(rowData) => {
                const paymentType = (rowData.type || "").toLowerCase();
                const allowedTypes = ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"];
                const isAllowedType = allowedTypes.some(
                  (type) => paymentType === type || paymentType.includes(type.replace("-", "")),
                );
                let displayType = rowData.type || "Unknown";
                if (paymentType === "cash") displayType = "Cash";
                else if (paymentType === "mpesa-transfer" || paymentType.includes("mpesa")) displayType = "M-Pesa";
                else if (paymentType === "bank-transfer" || paymentType.includes("bank")) displayType = "Bank Transfer";
                else if (paymentType === "cheque" || paymentType === "check") displayType = "Cheque";
                else if (paymentType === "payment") displayType = "Payment";
                return (
                  <div>
                    {displayType}
                    {isAllowedType && (
                      <i
                        className="fas fa-receipt ml-2"
                        style={{ color: "#28a745", fontSize: "0.85em", marginLeft: "5px" }}
                        title="Receipt available"
                      />
                    )}
                  </div>
                );
              }}
            />
            <Column field="paymentReference" header="Reference" sortable />
            <Column
              field="paymentCompletion"
              header="Status"
              sortable
              body={(rowData) => (
                <span
                  className={`badge ${rowData.paymentCompletion === "Completed"
                      ? "bg-success"
                      : rowData.paymentCompletion === "Partial"
                        ? "bg-warning"
                        : rowData.paymentCompletion === "Overpaid"
                          ? "bg-info"
                          : "bg-secondary"
                    }`}
                >
                  {rowData.paymentCompletion}
                </span>
              )}
            />
            <Column
              body={actionBodyTemplate}
              exportable={false}
              style={{ width: "4rem" }}
            />
          </DataTable>
        </Dialog>

        {/* ── Printable Invoice ─────────────────────────────────────────────────── */}
        <div className={styles.invoiceContainer}>
          <div id="printable-invoice" className={styles.invoiceContent}>

            {/* Status banner */}
            <div
              className={styles.statusBanner}
              data-status={invoice?.status?.toLowerCase()}
            >
              <span>{invoice?.status || "PENDING"}</span>
            </div>

            {/* ── Invoice Header ──────────────────────────────────────────────── */}
            <div className={styles.invoiceHeader}>
              <div className={styles.invoiceHeaderContent}>
                <div className={styles.headerTitle}>
                  <h1 style={{ margin: 0, color: "white", fontSize: "28px", fontWeight: "600" }}>
                    {isVoided ? "CREDIT NOTE" : "INVOICE"}
                  </h1>
                </div>
                <div className={styles.headerDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE NO.</span>
                    <span className={styles.value}>{invoice?.invoiceNumber || "N/A"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>ACCOUNT NO.</span>
                    <span className={styles.value}>{invoice?.accountNumber || "N/A"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE DATE</span>
                    <span className={styles.value}>{formatDate(invoice?.issueDate)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>DUE DATE</span>
                    <span className={styles.value}>{formatDate(invoice?.dueDate)}</span>
                  </div>
                </div>
              </div>

              {/* Logo below header */}
              <div className={styles.logoContainer} style={{ marginTop: "1rem", justifyContent: "center" }}>
                {facilityLogo ? (
                  <img
                    src={facilityLogo}
                    alt="Company Logo"
                    className={styles.companyLogo}
                    onError={(e) => {
                      e.target.style.display = "none";
                      console.error("Failed to load logo:", facilityLogo);
                    }}
                  />
                ) : (
                  <div className={styles.logoPlaceholder} style={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                    <span>Company Logo</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Invoice Body ────────────────────────────────────────────────── */}
            <div className={styles.invoiceBody}>

              {/* Address section */}
              <div className={styles.addressSection}>
                <div className={styles.addressLayout}>
                  <div className={styles.fromSection}>
                    <h2 className={styles.sectionTitle}>FROM</h2>
                    <div className={styles.addressContent}>
                      <div>{invoice?.facility?.name || "N/A"} {invoice?.unit?.name}</div>
                      <div>Property Management</div>
                    </div>
                  </div>

                  <div className={styles.billToSection}>
                    <h2 className={styles.sectionTitle}>BILL TO</h2>
                    <div className={styles.addressContent}>
                      <div>{invoice?.customerInfo?.fullName || "N/A"}</div>
                      <div>Unit: {invoice?.unit?.name || "N/A"}</div>
                    </div>
                  </div>

                  {/* TOTAL DUE shown in address row only for non-voided invoices */}
                  {!isVoided && (
                    <div className={styles.totalSection}>
                      <h2 className={styles.sectionTitle}>TOTAL DUE</h2>
                      <div className={`${styles.totalAmount} ${isFinalCredit ? styles.creditAmount : ""}`}>
                        {isFinalCredit
                          ? formatAmount(-Math.abs(calculateTotalDue()))
                          : formatAmount(calculateTotalDue())}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Items grid */}
              <div className={styles.itemsGrid}>
                <div className={styles.gridHeader}>
                  <span className={styles.columnHeader}>DESCRIPTION</span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>UNIT PRICE</span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>QTY</span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>AMOUNT</span>
                </div>

                {invoice?.items?.map((item, index) => (
                  <div key={index} className={styles.gridRow}>
                    <span className={styles.value}>{item.description || "N/A"}</span>
                    <span className={`${styles.value} ${styles.right}`}>{formatAmount(item.unitPrice)}</span>
                    <span className={`${styles.value} ${styles.right}`}>{item.quantity || "N/A"}</span>
                    <span className={`${styles.value} ${styles.right}`}>{formatAmount(item.quantity * item.unitPrice)}</span>
                  </div>
                ))}

                {/* ── Calculations ──────────────────────────────────────────── */}
                <div className={styles.calculations}>
                  <div className={styles.calculationsGrid}>

                    {/* Subtotal */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>SUBTOTAL</span>
                      <span className={styles.calcValue}>{formatAmount(invoice?.subTotal)}</span>
                    </div>

                    {/* Tax rows */}
                    {invoice?.taxes && invoice.taxes.length > 0 ? (
                      invoice.taxes.map((taxLine, idx) => (
                        <div key={idx} className={styles.calculationsRow}>
                          <span className={styles.calcLabel}>
                            {taxLine.name} ({taxLine.percentage}%)
                          </span>
                          <span className={styles.calcValue}>{formatAmount(taxLine.amount)}</span>
                        </div>
                      ))
                    ) : (
                      invoice?.tax > 0 && (
                        <div className={styles.calculationsRow}>
                          <span className={styles.calcLabel}>
                            TAX ({invoice?.subTotal ? ((invoice.tax / invoice.subTotal) * 100).toFixed(0) : 0}%)
                          </span>
                          <span className={styles.calcValue}>{formatAmount(invoice?.tax)}</span>
                        </div>
                      )
                    )}

                    {/* Invoice total */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>INVOICE TOTAL</span>
                      <span className={styles.calcValue}>{formatAmount(invoice?.totalAmount)}</span>
                    </div>

                    {/* ── The rows below are hidden for voided invoices ──────── */}
                    {!isVoided && (
                      <>
                        <div className={styles.divider}></div>

                        {/* Balance Brought Forward */}
                        {hasBalanceBroughtForward() && (
                          <div className={styles.calculationsRow}>
                            <span className={styles.calcLabel}>BALANCE B/FORWARD</span>
                            <span
                              className={`${styles.calcValue} ${isCredit ? styles.creditAmount : styles.debitAmount}`}
                            >
                              {isCredit
                                ? "(" + formatBalanceBroughtForward() + ")"
                                : formatBalanceBroughtForward()}
                            </span>
                          </div>
                        )}

                        {/* Amount Payable */}
                        <div className={styles.calculationsRow}>
                          <span className={styles.calcLabel}>AMOUNT PAYABLE</span>
                          <span className={styles.calcValue}>{formatAmount(calculateTotalBalance())}</span>
                        </div>

                        {/* Penalty */}
                        {((invoice?.penalty && parseFloat(invoice.penalty) > 0) ||
                          (invoice?.invoiceNote && invoice.invoiceNote.includes("Penalty applied"))) && (
                            <>
                              <div className={styles.calculationsRow}>
                                <span className={styles.calcLabel}>PENALTY</span>
                                <span
                                  className={styles.calcValue}
                                  style={{ color: "#dc3545", fontWeight: "600" }}
                                >
                                  {invoice.penalty ? formatAmount(invoice.penalty) : "Applied"}
                                </span>
                              </div>
                              {invoice?.invoiceNote && invoice.invoiceNote.includes("Penalty applied") && (
                                <div
                                  className={styles.calculationsRow}
                                  style={{ fontSize: "0.85em", fontStyle: "italic" }}
                                >
                                  <span className={styles.calcLabel}></span>
                                  <span
                                    className={styles.calcValue}
                                    style={{ color: "#6c757d", fontSize: "0.85em" }}
                                  >
                                    {invoice.invoiceNote.replace("Penalty applied: ", "")}
                                  </span>
                                </div>
                              )}
                            </>
                          )}

                        <div className={styles.divider}></div>

                        {/* Payment */}
                        <div className={styles.calculationsRow}>
                          <span className={styles.calcLabel}>PAYMENT</span>
                          <span className={styles.calcValue}>{formatAmount(invoice?.amountPaid)}</span>
                        </div>

                        {/* Total Due */}
                        <div className={`${styles.calculationsRow} ${styles.balance}`}>
                          <span className={styles.calcLabel}>TOTAL DUE</span>
                          <span
                            className={`${styles.calcValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount}`}
                          >
                            {isFinalCredit
                              ? formatAmount(-Math.abs(calculateTotalDue()))
                              : formatAmount(calculateTotalDue())}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Footer ────────────────────────────────────────────────────── */}
              {isVoided ? (
                <div
                  className={styles.footer}
                  style={{
                    backgroundColor: "#f8d7da",
                    borderTop: "2px solid #dc3545",
                    textAlign: "center",
                  }}
                >
                  <span style={{ color: "#721c24", fontWeight: "500" }}>This invoice has been voided</span>
                </div>
              ) : (
                <div className={styles.footer}>
                  <div className="row align-items-center justify-content-center">
                    <div className="col-auto text-center">
                      <p style={{ marginTop: "5px", marginBottom: "2px", fontSize: "10px" }}>
                        ___________________________
                      </p>
                      <p className="text-muted" style={{ fontSize: "9px", marginBottom: 0 }}>
                        Authorized Signature
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PMInvoicePage;